/**
 * @fileOverView
 * @description Developer Model<br>
 * @copyright 2015-present Flatirontek LLC
 **/

const AmpersandModel = require('ampersand-model');
const app = require("ampersand-app");
const dialog = require("../lib/dialog");

const Developer = AmpersandModel.extend({

    urlRoot: function(){
        return app.config.restApiUrl + "/developer";
    },

    ajaxConfig: app.utils.setPersonalAuthTokenHeader,

    props: {
        oauth_client_id: 'string',
        oauth_client_secret: 'string',
        oauth_service_name: 'string',
        oauth_redirect_uri: 'string',
        oauth_redirect_uris: ['array', true, function() { return []; }],
        api_key: 'string',
        webhooks: ['array', true, function() { return []; }],
        webhooks_secret: 'string'
    },

    session: {
        editing: ['boolean', true, false]
    },

    isNew: function() {
        return !this.oauth_client_id || this.oauth_client_id.length === 0;
    },

    url: function() {
        return this.urlRoot();
    },

    derived: {
        maskedClientSecret: {
            deps: ['oauth_client_secret'],
            fn: function() {
                if (!this.oauth_client_secret) return '';
                return this.oauth_client_secret.substring(0, 8) + '***';
            }
        },

        maskedApiKey: {
            deps: ['api_key'],
            fn: function() {
                if (!this.api_key) return '';
                return this.api_key.substring(0, 8) + '***';
            }
        },

        displayServiceName: {
            deps: ['oauth_service_name'],
            fn: function() {
                return this.oauth_service_name || 'Unknown Service';
            }
        },

        configStatus: {
            deps: ['oauth_client_id', 'oauth_redirect_uri', 'oauth_redirect_uris'],
            fn: function() {
                const hasClientId = this.oauth_client_id && this.oauth_client_id.length > 0;
                const hasRedirectUri = this.redirectUris().length > 0;
                
                if (hasClientId && hasRedirectUri) return 'Complete';
                if (hasClientId || hasRedirectUri) return 'Partial';
                return 'Incomplete';
            }
        },

        configStatusClass: {
            deps: ['configStatus'],
            fn: function() {
                switch (this.configStatus) {
                    case 'Complete': return 'text-success';
                    case 'Partial': return 'text-warning';
                    default: return 'text-danger';
                }
            }
        },

        //one derived value per REDIRECT URI input in the developer portal
        oauth_redirect_uri_1: {
            deps: ['oauth_redirect_uris', 'oauth_redirect_uri'],
            fn: function() { return this.redirectUris()[0] || ''; }
        },

        oauth_redirect_uri_2: {
            deps: ['oauth_redirect_uris', 'oauth_redirect_uri'],
            fn: function() { return this.redirectUris()[1] || ''; }
        },

        oauth_redirect_uri_3: {
            deps: ['oauth_redirect_uris', 'oauth_redirect_uri'],
            fn: function() { return this.redirectUris()[2] || ''; }
        },

        maskedWebhooksSecret: {
            deps: ['webhooks_secret'],
            fn: function() {
                if (!this.webhooks_secret) return '';
                return this.webhooks_secret.substring(0, 8) + '***';
            }
        },

        webhook_url_1: {
            deps: ['webhooks'],
            fn: function() {
                return (this.webhooks && this.webhooks[0]) ? this.webhooks[0].url : '';
            }
        },

        webhook_url_2: {
            deps: ['webhooks'],
            fn: function() {
                return (this.webhooks && this.webhooks[1]) ? this.webhooks[1].url : '';
            }
        },

        webhook_url_3: {
            deps: ['webhooks'],
            fn: function() {
                return (this.webhooks && this.webhooks[2]) ? this.webhooks[2].url : '';
            }
        },

        webhook_disabled_1: {
            deps: ['webhooks'],
            fn: function() {
                return (this.webhooks && this.webhooks[0]) ? this.webhooks[0].failures >= 100 : false;
            }
        },

        webhook_disabled_2: {
            deps: ['webhooks'],
            fn: function() {
                return (this.webhooks && this.webhooks[1]) ? this.webhooks[1].failures >= 100 : false;
            }
        },

        webhook_disabled_3: {
            deps: ['webhooks'],
            fn: function() {
                return (this.webhooks && this.webhooks[2]) ? this.webhooks[2].failures >= 100 : false;
            }
        },

        webhooksInstructions: {
            deps: ['webhooks', 'webhooks_secret'],
            fn: function() {
                var timestamp = Date.now();
                var secret = this.webhooks_secret ? this.maskedWebhooksSecret : 'your-webhook-secret';

                return '<div class="vdesc" data-timestamp="' + timestamp + '">' +
                    '<p>Outbound webhooks send a POST request to your specified URLs whenever any of your vybits are triggered. ' +
                    'The request body is identical to a log record from the <a href="https://developer.vybit.net/api-reference" target="_blank">Developer API</a>, ' +
                    'so you can use the same parsing logic for both polling and push-based integrations.</p>' +

                    '<p><strong>Payload:</strong></p>' +
                    '<code class="voauth-code-block">' +
                    '{<br/>' +
                    '&nbsp;&nbsp;"key": "bbxope6xhryminef",<br/>' +
                    '&nbsp;&nbsp;"vybKey": "t8a77piezgcolz70",<br/>' +
                    '&nbsp;&nbsp;"vybName": "Deploy Alert",<br/>' +
                    '&nbsp;&nbsp;"createdAt": "2026-04-01T18:30:00.000Z",<br/>' +
                    '&nbsp;&nbsp;"vybDescription": "Production deployment notification",<br/>' +
                    '&nbsp;&nbsp;"soundKey": "k9m2xvqr4hntyjw3",<br/>' +
                    '&nbsp;&nbsp;"vybfollowKey": "p3r7wdfl5qzxmc08",<br/>' +
                    '&nbsp;&nbsp;"ownerName": "Jane",<br/>' +
                    '&nbsp;&nbsp;"senderName": "Jane",<br/>' +
                    '&nbsp;&nbsp;"notification": "v2.4.1 deployed to production",<br/>' +
                    '&nbsp;&nbsp;"log": "Build #847 completed in 3m 12s",<br/>' +
                    '&nbsp;&nbsp;"imageUrl": "https://example.com/build-status.png",<br/>' +
                    '&nbsp;&nbsp;"linkUrl": "https://dashboard.example.com/deploys/847"<br/>' +
                    '}</code>' +

                    '<p><strong>Signature Verification:</strong><br/>' +
                    'Each request includes an <code>X-Vybit-Signature</code> header containing an HMAC-SHA256 hex digest of the JSON body, signed with your webhook secret. Verify it server-side:</p>' +
                    '<code class="voauth-code-block">' +
                    'const crypto = require(\'crypto\');<br/><br/>' +
                    'const expected = crypto<br/>' +
                    '&nbsp;&nbsp;.createHmac(\'sha256\', \'' + secret + '\')<br/>' +
                    '&nbsp;&nbsp;.update(requestBody)<br/>' +
                    '&nbsp;&nbsp;.digest(\'hex\');<br/><br/>' +
                    'if (signature !== expected) {<br/>' +
                    '&nbsp;&nbsp;// reject request<br/>' +
                    '}</code>' +

                    '<p><strong>Delivery:</strong><br/>' +
                    'Webhooks are delivered asynchronously and do not affect notification delivery. ' +
                    'Failed deliveries are retried up to 3 times with exponential backoff. ' +
                    'Your endpoint must respond with a 2xx status code within 10 seconds. ' +
                    'A URL that fails 100 consecutive times is automatically disabled and marked as DISABLED above. Click RE-ENABLE to reset the failure count and resume delivery.</p>' +
                    '</div>';
            }
        },

        apiInstructions: {
            deps: ['api_key'],
            fn: function() {
                const apiKey = this.api_key ? this.maskedApiKey : 'your-api-key';
                const timestamp = Date.now();

                // Construct API reference URL based on environment
                let developerHost = 'developer.vybit.net';
                if (app.config.env === "development") {
                    developerHost = 'dev.developer.vybit.net';
                }
                const apiReferenceUrl = `https://${developerHost}/api-reference`;

                return `
                    <div class="vdesc" data-timestamp="${timestamp}">
                    <p>The Vybit Developer API provides programmatic access to manage vybits and send notifications from your backend services. Use your API key to authenticate requests and integrate Vybit into your automation workflows, monitoring systems, or custom applications.</p>

                    <p><strong>Resources:</strong><br/>
                    • <a href="${apiReferenceUrl}" target="_blank">API Reference Documentation</a> - Complete endpoint reference with examples<br/>
                    • <a href="https://www.npmjs.com/package/@vybit/api-sdk" target="_blank">NPM Package</a> - Official TypeScript/JavaScript SDK<br/>
                    • <a href="https://gitlab.com/flatirontek/vybit-sdk" target="_blank">SDK Repository</a> - Source code and examples
                    </p>

                    <p><strong>Quick Start:</strong></p>
                    <code class="voauth-code-block">
                    # Install the SDK<br/>
                    npm install @vybit/api-sdk<br/><br/>

                    # Initialize the client<br/>
                    const { VybitAPIClient } = require('@vybit/api-sdk');<br/><br/>

                    const client = new VybitAPIClient({<br/>
                    &nbsp;&nbsp;apiKey: '${apiKey}'<br/>
                    });<br/><br/>

                    # Get usage metrics<br/>
                    const meter = await client.getMeter();<br/><br/>

                    # Trigger a vybit<br/>
                    const result = await client.triggerVybit('vybit-key', {<br/>
                    &nbsp;&nbsp;message: 'Hello from the API!'<br/>
                    });
                    </code>

                    <p><strong>Authentication:</strong><br/>
                    All API requests require the <code>X-API-Key</code> header with your API key. The SDK handles this automatically.</p>
                    </div>
                `;
            }
        },

        mcpInstructions: {
            deps: ['api_key'],
            fn: function() {
                const apiKey = this.api_key ? this.maskedApiKey : 'your-api-key';
                const timestamp = Date.now();

                return `
                    <div class="vdesc" data-timestamp="${timestamp}">
                    <p>The <a href="https://modelcontextprotocol.io" target="_blank">Model Context Protocol (MCP)</a> enables AI assistants like Claude and ChatGPT to interact with Vybit directly: managing vybits, triggering notifications, and more through natural language. Vybit provides both a hosted remote MCP server and a standalone npm package.</p>

                    <hr/>
                    <strong>Hosted Remote MCP Server (Easiest)</strong>
                    <p>Vybit provides a hosted remote MCP server. No installation or API key management required. Connect directly from Claude or ChatGPT:</p>
                    <code class="voauth-code">https://api.vybit.net/v1/mcp</code><br/><br/>

                    <p><strong>Protocol:</strong> Streamable HTTP (JSON-RPC 2.0)<br/>
                    <strong>Authentication:</strong> OAuth2 (automatic) or API Key (<code>X-API-Key</code> header)<br/>
                    <strong>Tools:</strong> 30 tools including vybit management, triggers, reminders, sounds, logs, subscriptions, and more</p>

                    <hr/>
                    <strong>Connecting Claude</strong><br/><br/>

                    <strong>Claude Desktop / Claude Web (claude.ai)</strong><br/>
                    <ol>
                        <li>In any chat, click <b>+ &rarr; Add connector &rarr; Add custom connector</b> (or go to <b>Customize &rarr; Connectors</b>, click <b>+ Add</b>, and choose <b>Add custom connector</b>)</li>
                        <li>Name it <b>Vybit</b> and enter the MCP URL: <code>https://api.vybit.net/v1/mcp</code></li>
                        <li>Leave the authentication settings at their defaults and click <b>Add</b></li>
                        <li>Sign in to your Vybit account and click <b>Allow</b></li>
                    </ol>
                    Works on Claude's free plan.<br/><br/>

                    <strong>Claude Code (CLI)</strong><br/>
                    Add to the current project:<br/>
                    <code class="voauth-code-block">
                    claude mcp add vybit -e VYBIT_API_KEY=${apiKey} -- npx -y @vybit/mcp-server
                    </code><br/>
                    Or add across all projects (user-scoped):<br/>
                    <code class="voauth-code-block">
                    claude mcp add vybit -s user -e VYBIT_API_KEY=${apiKey} -- npx -y @vybit/mcp-server
                    </code><br/>

                    <hr/>
                    <strong>Connecting ChatGPT</strong><br/><br/>

                    <strong>ChatGPT Web / Desktop / Mobile</strong><br/>
                    <ol>
                        <li>Open <b>Plugins</b> in the ChatGPT sidebar (or go to <a href="https://chatgpt.com/plugins" target="_blank">chatgpt.com/plugins</a>)</li>
                        <li>Search for <b>Vybit</b> and select it</li>
                        <li>Click <b>Install plugin</b>, then <b>Connect</b> if asked</li>
                        <li>Sign in to your Vybit account and click <b>Allow</b></li>
                    </ol>
                    Vybit is an <a href="https://chatgpt.com/plugins/plugin_asdk_app_6ac844e62c388191bcc87cd5faaeee2a" target="_blank">official</a> ChatGPT plugin, so there is no server URL to enter.<br/><br/>

                    Once connected, all 30 MCP tools are available immediately through natural conversation.<br/><br/>

                    <hr/>
                    <strong>Local Installation (@vybit/mcp-server)</strong><br/><br/>

                    <p>If you prefer to run the server locally (e.g., for Claude Code, Cline, or other MCP clients that support stdio transport), install the npm package and configure with your API key.</p>

                    <strong>Claude Desktop (MCPB):</strong><br/>
                    Download the latest <code>.mcpb</code> bundle from the <a href="https://gitlab.com/flatirontek/vybit-sdk/-/releases" target="_blank">releases page</a> and double-click to install. Claude Desktop will prompt you for your Vybit API key during setup.<br/><br/>

                    <strong>Claude Desktop (Manual):</strong><br/>
                    <code class="voauth-code-block">
                    npm install -g @vybit/mcp-server
                    </code><br/>
                    Add to your Claude Desktop config file:<br/>
                    <small><b>MacOS:</b> ~/Library/Application Support/Claude/claude_desktop_config.json<br/>
                    <b>Windows:</b> %APPDATA%\\Claude\\claude_desktop_config.json</small><br/>
                    <code class="voauth-code-block">
                    {<br/>
                    &nbsp;&nbsp;"mcpServers": {<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;"vybit": {<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"command": "npx",<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"args": ["-y", "@vybit/mcp-server"],<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"env": {<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"VYBIT_API_KEY": "${apiKey}"<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;}<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;}<br/>
                    &nbsp;&nbsp;}<br/>
                    }
                    </code><br/>

                    <strong>Troubleshooting: nvm Users</strong><br/>
                    GUI applications like Claude Desktop do not source your shell profile (<code>.bashrc</code>/<code>.zshrc</code>), so <code>node</code> and <code>npx</code> may resolve to the wrong Node version. If you see errors like <code>SyntaxError: Unexpected token {</code> or <code>Cannot find module 'node:path'</code>, skip <code>npx</code> and use <code>node</code> directly with absolute paths:<br/>
                    <code class="voauth-code-block">
                    # Find your node and global modules paths<br/>
                    which node<br/>
                    npm root -g
                    </code><br/>
                    Then use those paths in your config:<br/>
                    <code class="voauth-code-block">
                    {<br/>
                    &nbsp;&nbsp;"mcpServers": {<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;"vybit": {<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"command": "/Users/you/.nvm/versions/node/v24.6.0/bin/node",<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"args": ["/Users/you/.nvm/versions/node/v24.6.0/lib/node_modules/@vybit/mcp-server/dist/index.js"],<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"env": {<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"VYBIT_API_KEY": "${apiKey}"<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;}<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;}<br/>
                    &nbsp;&nbsp;}<br/>
                    }
                    </code><br/>
                    Node 18+ is required.<br/><br/>

                    <strong>Other MCP Clients (Cline, Zed, Continue.dev, etc.):</strong><br/>
                    Install globally with <code>npm install -g @vybit/mcp-server</code>, then configure your client to run <code>npx -y @vybit/mcp-server</code> with <code>VYBIT_API_KEY</code> set in the environment.<br/><br/>

                    <p><strong>Resources:</strong><br/>
                    • <a href="https://www.npmjs.com/package/@vybit/mcp-server" target="_blank">NPM Package</a> - @vybit/mcp-server<br/>
                    • <a href="https://gitlab.com/flatirontek/vybit-sdk" target="_blank">SDK Repository</a> - Source code and documentation<br/>
                    • <a href="https://modelcontextprotocol.io" target="_blank">MCP Specification</a> - Protocol documentation
                    </p>
                    </div>
                `;
            }
        },

        cliInstructions: {
            deps: ['api_key'],
            fn: function() {
                const apiKey = this.api_key ? this.maskedApiKey : 'your-api-key';
                const timestamp = Date.now();

                return `
                    <div class="vdesc" data-timestamp="${timestamp}">
                    <p>The Vybit CLI provides command-line access to the Vybit Developer API. All output is structured JSON to stdout, with errors to stderr and deterministic exit codes, making it suitable for shell scripting, CI/CD pipelines, and AI agent tooling.</p>

                    <hr/>
                    <strong>Installation</strong><br/><br/>
                    <code class="voauth-code-block">
                    npm install -g @vybit/cli
                    </code><br/>

                    <hr/>
                    <strong>Authentication</strong><br/><br/>
                    Configure your API key (saved to <code>~/.config/vybit/config.json</code>):<br/>
                    <code class="voauth-code-block">
                    vybit auth setup --api-key '${apiKey}'
                    </code><br/>
                    Credentials are resolved in this order:<br/>
                    <ol>
                        <li>CLI flag (<code>--api-key</code> or <code>--access-token</code>)</li>
                        <li>Environment variable (<code>VYBIT_API_KEY</code> or <code>VYBIT_ACCESS_TOKEN</code>)</li>
                        <li>Config file (<code>~/.config/vybit/config.json</code>)</li>
                    </ol>

                    <hr/>
                    <strong>Commands</strong><br/><br/>

                    <code class="voauth-code-block">
                    # List your vybits<br/>
                    vybit vybits list<br/><br/>

                    # Create a webhook-triggered vybit<br/>
                    vybit vybits create --name "Deploy Alert" --trigger-type webhook<br/><br/>

                    # Trigger a notification with a custom message<br/>
                    vybit trigger &lt;vybit-key&gt; --message "Build complete"<br/><br/>

                    # Trigger with image and link<br/>
                    vybit trigger &lt;vybit-key&gt; --message "New release" \\<br/>
                    &nbsp;&nbsp;--image-url "https://example.com/screenshot.png" \\<br/>
                    &nbsp;&nbsp;--link-url "https://dashboard.example.com"<br/><br/>

                    # Manage reminders<br/>
                    vybit reminders list --vybit-key &lt;key&gt;<br/>
                    vybit reminders create --vybit-key &lt;key&gt; --cron "0 9 * * *" --timezone "America/Denver"<br/><br/>

                    # Browse and subscribe to public vybits<br/>
                    vybit browse list --search "weather"<br/>
                    vybit subscriptions create --subscription-key &lt;key&gt;<br/><br/>

                    # View logs and usage<br/>
                    vybit logs list<br/>
                    vybit meter<br/><br/>

                    # Manage access<br/>
                    vybit peeps create --vybit-key &lt;key&gt; --email "user@example.com"
                    </code><br/>

                    <strong>Available Command Groups:</strong>
                    <ul>
                        <li><code>vybit vybits</code> &ndash; list, get, create, update, delete</li>
                        <li><code>vybit trigger</code> &ndash; trigger a notification</li>
                        <li><code>vybit reminders</code> &ndash; list, create, update, delete</li>
                        <li><code>vybit sounds</code> &ndash; list, get</li>
                        <li><code>vybit subscriptions</code> &ndash; list, get, create, update, delete</li>
                        <li><code>vybit browse</code> &ndash; list, get (public vybits)</li>
                        <li><code>vybit logs</code> &ndash; list, get, vybit, subscription</li>
                        <li><code>vybit peeps</code> &ndash; list, get, create, delete, vybit</li>
                        <li><code>vybit meter</code> &ndash; API usage metrics</li>
                        <li><code>vybit auth</code> &ndash; setup, status, logout</li>
                    </ul>

                    <hr/>
                    <strong>Output Modes</strong><br/><br/>
                    <ul>
                        <li><b>Default:</b> Pretty-printed JSON to stdout</li>
                        <li><b>Quiet (<code>-q</code>):</b> Keys/IDs only, one per line. Useful for piping and scripting.</li>
                        <li><b>Errors:</b> Structured JSON to stderr with exit code 1 (error) or 2 (auth error)</li>
                    </ul>

                    <p><strong>Resources:</strong><br/>
                    • <a href="https://www.npmjs.com/package/@vybit/cli" target="_blank">NPM Package</a> - @vybit/cli<br/>
                    • <a href="https://gitlab.com/flatirontek/vybit-sdk" target="_blank">SDK Repository</a> - Source code and documentation
                    </p>
                    </div>
                `;
            }
        },

        oauthInstructions: {
            deps: ['oauth_client_id', 'oauth_client_secret', 'oauth_service_name', 'oauth_redirect_uri', 'oauth_redirect_uris'],
            fn: function() {
                const clientId = this.oauth_client_id || 'your-client-id';
                const clientSecret = this.oauth_client_secret ? this.maskedClientSecret : 'your-client-secret'
                const redirectUri = this.redirectUris()[0] || 'https://yourapp.com/oauth/callback';
                const serviceName = this.oauth_service_name || 'Your Service';
                
                const timestamp = Date.now();

                const currentDomain = app.config.baseApiUrl;
                let serviceAuthDomain = app.config.firebase.serviceAuthDomain;
                if(app.config.env === "development") serviceAuthDomain = "dev." + serviceAuthDomain;

                // Construct OAuth reference URL based on environment
                let developerHost = 'developer.vybit.net';
                if (app.config.env === "development") {
                    developerHost = 'dev.developer.vybit.net';
                }
                const apiReferenceUrl = `https://${developerHost}/api-reference`;
                const oauthReferenceUrl = `https://${developerHost}/oauth-reference`;

                return `
                    <div class="vdesc" data-timestamp="${timestamp}">
                    <p>OAuth 2.0 integration allows your users to connect their Vybit accounts to your service. Once connected, your application can access their vybits and send personalized notifications on their behalf. Configure your service details above, then follow the implementation steps below.
                    </p>
                    
                        <strong>OAuth v2 Steps:</strong><br/><br/>
                        
                        <strong>1. Authorization Request</strong><br/>
                        Direct your users to:<br/>
                        <code class="voauth-code">https://${serviceAuthDomain}?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&state=RANDOM_STATE</code><br/><br/>
                        
                        <b>state</b> can be any random string between 8 and 512 characters.
                        <b>redirect_uri</b> must exactly match one of the redirect URIs configured above (up to 3, e.g. production, staging, and <code>http://localhost</code> for local development).<br/><br/>
                        
                        <strong>2. Authorization Grant</strong><br/>
                        Once the user approves access Vybit redirects them back to your configured redirect uri with a provided AUTHORIZATION_CODE to request an access token in the next step:<br/>
                        <code class="voauth-code">${redirectUri}?code=AUTHORIZATION_CODE&state=RANDOM_STATE</code><br/><br/>
                        
                        Verify that the state value matches what was sent in the request to prevent CSRF attacks. 
                        Note that if the user denies access the redirect URI will be appended with the query parameter error=access_denied.<br/><br/>
                        
                        <button type="button" class="btn btn-primary btn-sm test-auth-button" style="margin: 10px 0;">
                            Test Authorization and Redirect<br/><small>(using this account)</small>
                        </button><br/><br/>
                        
                        <strong>3. Access Token Request</strong><br/>
                        Make the following request from your service backend to request an access token using the provided AUTHORIZATION_CODE as follows:<br/>
                        <code class="voauth-code-block">
                        POST https://${serviceAuthDomain}/service/token<br/>
                        Content-Type: application/x-www-form-urlencoded<br/><br/>

                        grant_type=authorization_code<br/>
                        code=AUTHORIZATION_CODE<br/>
                        client_id=${clientId}<br/>
                        client_secret= ${clientSecret}
                        </code><br/>

                        <strong>PKCE Alternative (Public Clients)</strong><br/>
                        For public clients that cannot securely store a client_secret (native apps, SPAs, MCP clients), use <a href="https://datatracker.ietf.org/doc/html/rfc7636" target="_blank">PKCE (RFC 7636)</a> instead:<br/><br/>

                        In Step 1, include a <b>code_challenge</b> in the authorization request:<br/>
                        <code class="voauth-code">https://${serviceAuthDomain}?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&state=RANDOM_STATE&code_challenge=BASE64URL_SHA256_HASH&code_challenge_method=S256</code><br/><br/>

                        In Step 3, send <b>code_verifier</b> instead of client_secret:<br/>
                        <code class="voauth-code-block">
                        POST https://${serviceAuthDomain}/service/token<br/>
                        Content-Type: application/x-www-form-urlencoded<br/><br/>

                        grant_type=authorization_code<br/>
                        code=AUTHORIZATION_CODE<br/>
                        client_id=${clientId}<br/>
                        code_verifier=ORIGINAL_RANDOM_STRING
                        </code><br/>

                        The <b>code_verifier</b> is a random 43-128 character string. The <b>code_challenge</b> is its base64url-encoded SHA-256 hash. See the <a href="https://www.npmjs.com/package/@vybit/oauth2-sdk" target="_blank">@vybit/oauth2-sdk</a> for helper functions.<br/><br/>
                        
                        <strong id="token-response-header">Example Response:</strong><br/>
                        <code class="voauth-code-block" id="token-response-example">
                        {<br/>
                        &nbsp;&nbsp;"access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",<br/>
                        &nbsp;&nbsp;"token_type": "Bearer"<br/>
                        }
                        </code><br/>
                        
                        <button type="button" class="btn btn-primary btn-sm test-token-button" style="margin: 10px 0;">
                            Test Token Request<br/><small>(do the Test Authorization first)</small>
                        </button><br/><br/>
                        
                        <strong>4. Verify Authorization</strong><br/>
                        Use the access token to make authorized API calls, e.g., here is a test endpoint to verify the authentication:<br/>
                        <code class="voauth-code-block">
                        GET ${currentDomain}/service/test<br/>
                        Authorization: Bearer ACCESS_TOKEN
                        </code><br/>
                        
                        <strong id="verify-response-header">Example Response:</strong><br/>
                        <code class="voauth-code-block" id="verify-response-example">
                        {<br/>
                        &nbsp;&nbsp;"status": "ok"<br/>
                        }
                        </code><br/>
                        
                        <button type="button" class="btn btn-primary btn-sm test-verify-button" style="margin: 10px 0;">
                            Test Authorization Verification<br/><small>(requires Test Token Request first)</small>
                        </button><br/><br/>
                        
                        <strong>5. API Access</strong><br/>
                        To retrieve a list of the user's vybits, make the following request:<br/>
                        <code class="voauth-code-block">
                        GET ${currentDomain}/v1/vybits<br/>
                        Authorization: Bearer ACCESS_TOKEN
                        </code><br/>

                        <strong id="vybit-list-response-header">Example Response:</strong><br/>
                        <code class="voauth-code-block" id="vybit-list-response-example" style="max-height: 200px; overflow-y: auto; display: block;">
                        [<br/>
                        &nbsp;&nbsp;{"key": "abc123", "name": "My Alert", "triggerKey": "def456", ...},<br/>
                        &nbsp;&nbsp;{"key": "ghi789", "name": "Notification Sound", "triggerKey": "jkl012", ...}<br/>
                        ]
                        </code><br/>
                        
                        <button type="button" class="btn btn-primary btn-sm test-vybit-list-button" style="margin: 10px 0;">
                            Test Vybit List Request<br/><small>(requires Test Token Request first)</small>
                        </button><br/><br/>
                        
                        <strong>6. Triggering Vybits</strong><br/>
                        Once the user's vybit list has been retrieved allow your user to select a vybit and set any of the optional parameters. Try it out here to see how the trigger request should be configured.<br/>
                        <select class="form-control" id="vybit-select" style="margin: 10px 0; max-width: 400px;" disabled>
                            <option value="">Run Test Vybit List Request first...</option>
                        </select><br/>
                        
                        <strong>Optional Parameters:</strong><br/>
                        <small>If any parameters are specified, the request will be sent as POST with JSON body.</small><br/><br/>
                        
                        <div style="margin: 10px 0;">
                            <label for="trigger-message" style="display: block; margin-bottom: 5px;"><strong>Message:</strong></label>
                            <input type="text" id="trigger-message" class="form-control vplaceholder" placeholder="Optional message to display with notification" style="max-width: 500px;">
                        </div>
                        
                        <div style="margin: 10px 0;">
                            <label for="trigger-image" style="display: block; margin-bottom: 5px;"><strong>Image URL:</strong></label>
                            <input type="url" id="trigger-image" class="form-control vplaceholder" placeholder="Optional image URL to attach to notification" style="max-width: 500px;">
                        </div>
                        
                        <div style="margin: 10px 0;">
                            <label for="trigger-link" style="display: block; margin-bottom: 5px;"><strong>Link URL:</strong></label>
                            <input type="url" id="trigger-link" class="form-control vplaceholder" placeholder="Optional redirect URL when notification is tapped" style="max-width: 500px;">
                        </div>
                        
                        <div style="margin: 10px 0;">
                            <label for="trigger-log" style="display: block; margin-bottom: 5px;"><strong>Log:</strong></label>
                            <textarea id="trigger-log" class="form-control vplaceholder" rows="3" placeholder="Optional content to append to the Vybit log&#10;(supports hyperlinks)" style="max-width: 500px; resize: vertical;"></textarea>
                        </div><br/>
                        
                        <button type="button" class="btn btn-primary btn-sm test-trigger-button" style="margin: 10px 0;">
                            Test Vybit Trigger<br/><small>(requires Test Vybit List first)</small>
                        </button><br/><br/>
                        
                        <strong id="trigger-request-header">Trigger Request:</strong><br/>
                        <code class="voauth-code-block" id="trigger-url-display">
                        POST ${currentDomain}/v1/vybit/{key}/trigger<br/>
                        Authorization: Bearer ACCESS_TOKEN<br/>
                        Content-Type: application/json<br/><br/>
                        Body: <br/>{}
                        </code><br/>
                        
                        <strong id="trigger-response-header">Example Response:</strong><br/>
                        <code class="voauth-code-block" id="trigger-response-example">
                        {<br/>
                        &nbsp;&nbsp;"result": 1,<br/>
                        &nbsp;&nbsp;"plk": "bbxope6xhryminef"<br/>
                        }
                        </code><br/>
                        <p><strong>Resources:</strong><br/>
                        • <a href="${oauthReferenceUrl}" target="_blank">OAuth2 Reference</a> - Complete OAuth2 endpoint documentation<br/>
                        • <a href="${apiReferenceUrl}" target="_blank">API Reference</a> - Developer API endpoint documentation with interactive examples<br/>
                        • <a href="https://www.npmjs.com/package/@vybit/oauth2-sdk" target="_blank">NPM Package</a> - Official OAuth2 SDK<br/>
                        • <a href="https://${app.config.wwwHost}#trigger" target="_blank">Triggering Vybits</a> - Additional documentation
                        </p>
                    </div>
                `;
            }
        }
    },

    patch: function(data, paramModel=null){
        const self = this;

        const key = Object.keys(data)[0];
        let value = data[key];

        if(typeof(value)=="string") value = value.replace( /(<([^>]+)>)/ig, '');

        // Handle webhook URL params -- collect into webhooks array
        var webhookMatch = key.match(/^webhook_url_(\d+)$/);
        if(webhookMatch) {
            var index = parseInt(webhookMatch[1]) - 1;
            if(value.length > 0 && !app.utils.isValidURL(value)){
                dialog.alert("Invalid URL");
                if(paramModel != null) paramModel.set('editing', false);
                return;
            }
            var urls = self.extractWebhookUrls();
            // Pad array to reach the index
            while(urls.length <= index) urls.push('');
            urls[index] = value;
            // Remove trailing empty entries
            while(urls.length > 0 && urls[urls.length - 1] === '') urls.pop();

            self.patchWebhooks(urls, function(error) {
                if(paramModel != null) paramModel.set('editing', false);
                if(error) dialog.alert('Error updating webhooks.');
            });
            return;
        }

        // Handle redirect URI params -- collect into oauth_redirect_uris array.
        // Order carries no meaning, so a cleared slot drops out of the list.
        var redirectMatch = key.match(/^oauth_redirect_uri_(\d+)$/);
        if(redirectMatch) {
            var slot = parseInt(redirectMatch[1]) - 1;
            if(value.length > 0 && !app.utils.isValidURL(value)){
                dialog.alert("Invalid URL");
                if(paramModel != null) paramModel.set('editing', false);
                return;
            }
            var uris = self.redirectUris();
            while(uris.length <= slot) uris.push('');
            uris[slot] = value;
            uris = uris.filter(function(uri) { return uri.length > 0; });

            self.patchRedirectUris(uris, function(error) {
                if(paramModel != null) paramModel.set('editing', false);
                if(error) dialog.alert(error && error.error ? error.error : 'Error updating redirect URIs.');
            });
            return;
        }

        data = {};
        data[key] = value;

        const isExisting = this.oauth_client_id && this.oauth_client_id.length > 0;
        const patch = isExisting ? true : false;

        this.save(data, {
            patch: patch,
            wait: true,
            success: function(model, response){
                if(paramModel != null) paramModel.set('editing', false);
                console.log('developer config updated successfully for key:', key);

                console.log('Config updated, vparam listeners should handle UI refresh');
            },
            error: function(model, response, options){
                console.log('error updating developer config:', response.body);

                if(response.statusCode == 409) {
                    dialog.alert('OAuth Client ID already exists. Please use a different client ID.');
                } else {
                    dialog.alert('Error updating developer configuration.');
                }

                if(paramModel != null) paramModel.set('editing', false);
            }
        });
    },

    rotateCredential: function(field, callback) {
        var data = {};
        data[field] = 'rotate';
        this.save(data, {
            patch: true,
            wait: true,
            success: function(model, response) {
                console.log(field + ' rotated successfully');
                if(callback) callback(null, model);
            },
            error: function(model, response, options) {
                console.log('error rotating ' + field + ':', response.body);
                if(callback) callback(response.body, null);
            }
        });
    },

    rotateSecret: function(callback) {
        this.rotateCredential('oauth_client_secret', callback);
    },

    rotateApiKey: function(callback) {
        this.rotateCredential('api_key', callback);
    },

    rotateWebhooksSecret: function(callback) {
        this.rotateCredential('webhooks_secret', callback);
    },

    // The registered redirect URIs; rows saved before the list existed only
    // carry oauth_redirect_uri
    redirectUris: function() {
        var list = Array.isArray(this.oauth_redirect_uris) ? this.oauth_redirect_uris : [];
        list = list.filter(function(uri) { return typeof uri === 'string' && uri.length > 0; });
        if(list.length === 0 && this.oauth_redirect_uri) list = [this.oauth_redirect_uri];
        return list;
    },

    patchRedirectUris: function(uris, callback) {
        this.save({oauth_redirect_uris: uris}, {
            patch: true,
            wait: true,
            success: function(model, response) {
                console.log('redirect URIs updated successfully');
                if(callback) callback(null, model);
            },
            error: function(model, response, options) {
                console.log('error updating redirect URIs:', response.body);
                if(callback) callback(response.body, null);
            }
        });
    },

    extractWebhookUrls: function() {
        return (this.webhooks || []).map(function(h) {
            return typeof h === 'object' ? h.url : h;
        });
    },

    patchWebhooks: function(webhooks, callback) {
        this.save({webhooks: webhooks}, {
            patch: true,
            wait: true,
            success: function(model, response) {
                console.log('webhooks updated successfully');
                if(callback) callback(null, model);
            },
            error: function(model, response, options) {
                console.log('error updating webhooks:', response.body);
                if(callback) callback(response.body, null);
            }
        });
    },

    reEnableWebhook: function(index, callback) {
        this.patchWebhooks(this.extractWebhookUrls(), callback);
    }

});

module.exports = Developer;